/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import isEmpty from 'lodash/isEmpty';

import { resetNewOptions, generateActiveTheme, applyTheme, createApiThunk } from '../../../shared/utils';
import { updateFormInventoryProducts } from '../../builder/slice-actions/form';
import { getPipedCtrlLabelsMap, formAmendForm } from '../../../shared/form-lib';
import blankDesignTheme from './default-styles';
import defaultDesign from './default-design';

const LOCATION = 'CUSTOMISE REDUCER';

export const defaultState = {
  labelPipedCtrlLabelsMap: {},
  loadingGoogleFonts: true,
  showMobileSideBar: false,
  themeTemplatesError: '',
  reportCustomise: false,
  isCustomising: false,
  googleFontsError: '',
  userCustomTexts: {},
  themeTemplates: [],
  actualView: false,
  customiseForm: {},
  activeTheme: [],
  googleFonts: [],
  loading: true,
  error: '',
  theme: {},
  form: {},
};

function amendThemeArray(response) {
  // filtering unpublished theme
  const newVersion3 = response?.version3?.filter((theme) => theme.published);
  newVersion3?.unshift(blankDesignTheme);
  return newVersion3 ?? [];
}

async function createFontsData(response) {
  const fonts = await response.json();
  return fonts.items.map((font) => ({
    label: font.family,
    value: font.family,
    text: font.family,
  }));
}

export const fetchFormById = createApiThunk({
  transformer: (response, formId) => updateFormInventoryProducts(response, formId),
  messages: { notFound: 'Requested form could not be found.' },
  url: (formId) => `/api/v2/user/form/${formId}`,
  typePrefix: 'customise/fetchFormById',
  action: 'fetch form',
  location: LOCATION,
});

export const fetchThemes = createApiThunk({
  transformer: (response) => amendThemeArray(response),
  url: '/api/v2/user/csstemplates?version=3',
  typePrefix: 'customise/fetchThemes',
  action: 'fetch themes',
  location: LOCATION,
});

export const fetchFonts = createApiThunk({
  url: 'https://www.googleapis.com/webfonts/v1/webfonts?sort=popularity&key=AIzaSyDNpMvZXvwITG-x3W3yOhQPjV89Io2Z6WI',
  transformer: (response) => createFontsData(response),
  typePrefix: 'customise/fetchFonts',
  fetcher: (_, url) => fetch(url),
  action: 'fetch Google Fonts',
  location: LOCATION,
});

const customiseSlice = createSlice({
  name: 'customise',
  initialState: defaultState,
  reducers: {
    doApplyTheme: (state, { payload }) => {
      const { themeName } = payload;
      const { customiseForm, themeTemplates } = state;
      window.mixpanel.track('Changed Form Theme', {
        themeName,
      });
      const selectedTheme = themeTemplates.find((theme) => theme.name === themeName);
      applyTheme(selectedTheme, customiseForm);
      customiseForm.design.theme = themeName;
    },
    updateFormOptionsSuccess: (state) => {
      state.isCustomising = false;
    },
    updateFormOptionsFailure: (state) => {
      return {
        ...state,
        customiseForm: state.form,
      };
    },
    updateCustomiseForm: (state, { payload }) => {
      const { customiseForm } = state;
      const { designCategory, designProp, value } = payload;
      if (designCategory === 'old') {
        customiseForm[designProp] = value;
      } else {
        customiseForm.design[designProp] = value;
      }
      state.isCustomising = true;
    },
    doResetCustomiseForm: (state) => {
      const { customiseForm, themeTemplates } = state;
      const themeName = customiseForm.design?.theme;
      if (themeName !== 'None') {
        // revert to original theme default
        const presentTheme = themeTemplates.find((theme) => theme.name === themeName);
        applyTheme(presentTheme, customiseForm);
      } else {
        // revert to blank design
        resetNewOptions(customiseForm);
        customiseForm.design.theme = 'None';
      }
      state.isCustomising = true;
    },
    displayMobileSidebar: (state, { payload }) => {
      state.showMobileSideBar = payload;
    },
    showReportFormInCustomise: (state, { payload }) => {
      state.reportCustomise = payload;
    },
    resetPipedCtrlLabelsMap: (state) => {
      state.labelPipedCtrlLabelsMap = defaultState.labelPipedCtrlLabelsMap;
    },
  },

  extraReducers: (customise) => {
    // FORM
    customise.addCase(fetchFormById.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    customise.addCase(fetchFormById.fulfilled, (state, { payload }) => {
      const form = formAmendForm(payload);

      state.userCustomTexts = payload.user_custom_texts;
      state.customiseForm = form;
      state.loading = false;
      state.form = form;

      state.labelPipedCtrlLabelsMap = getPipedCtrlLabelsMap(form?.definition?.pages ?? [], true);

      if (!form.design) {
        state.form.design = defaultDesign;
        state.customiseForm.design = defaultDesign;
      }

      if (!isEmpty(state.themeTemplates)) {
        state.activeTheme = generateActiveTheme(form.design, state.themeTemplates);
      }
    });
    customise.addCase(fetchFormById.rejected, (state, { payload }) => {
      state.error = payload.uiErrorMessage;
      state.loading = false;
    });

    // PUBLISHED THEMES
    customise.addCase(fetchThemes.pending, (state) => {
      state.themeTemplatesError = '';
      state.themeTemplates = [];
    });
    customise.addCase(fetchThemes.fulfilled, (state, { payload }) => {
      const { customiseForm } = state;
      
      const activeTheme = generateActiveTheme(customiseForm.design, payload);

      state.activeTheme = activeTheme;
      state.themeTemplates = payload;
      state.themeTemplatesError = '';
    });
    customise.addCase(fetchThemes.rejected, (state, { payload }) => {
      state.themeTemplatesError = payload.uiErrorMessage;
      state.themeTemplates = [];
    });

    // GOOGLE FONTS
    customise.addCase(fetchFonts.pending, (state) => {
      state.loadingGoogleFonts = true;
      state.googleFontsError = '';
      state.googleFonts = [];
    });
    customise.addCase(fetchFonts.fulfilled, (state, { payload }) => {
      state.loadingGoogleFonts = false;
      state.googleFonts = payload;
      state.googleFontsError = '';
    });
    customise.addCase(fetchFonts.rejected, (state, { payload }) => {
      state.googleFontsError = payload.uiErrorMessage;
      state.loadingGoogleFonts = false;
      state.googleFonts = [];
    });
  },
});

export const {
  showReportFormInCustomise,
  updateFormOptionsSuccess,
  updateFormOptionsFailure,
  resetPipedCtrlLabelsMap,
  doResetCustomiseForm,
  displayMobileSidebar,
  updateCustomiseForm,
  doApplyTheme,
} = customiseSlice.actions;

export default customiseSlice.reducer;
